import { Sparkles, Leaf, Compass } from 'lucide-react';

export const CASE_STUDIES = [
  {
    slug: 'astrologer-digital-transformation',
    industry: 'Astrology · Personal Brand',
    icon: Sparkles,
    accent: 'purple',
    client: 'Astrologer Digital Transformation',
    location: 'National & International Seekers',
    objective:
      'Build a premium digital presence for an established astrologer and grow qualified consultation bookings while expanding the practice to national and international seekers.',
    practitionerNote:
      'As a practising astrologer and Vastu consultant myself, I understood this client\u2019s world from the inside \u2014 the language, the sensitivities and the trust that guides this profession. That allowed me to carry their authentic voice into a global, professional brand rather than diluting it into generic marketing.',
    challenges: [
      'Over 80% of the practice came through referrals and word of mouth',
      'No structured online journey to guide seekers into a consultation',
      'Limited visibility in search and inconsistent social presence',
      'Website visitors rarely converted into booked sittings',
    ],
    strategy: [
      'Built a premium, trust-first personal brand rooted in the astrologer\u2019s authentic voice and lineage',
      'Designed a calm, high-converting consultation journey \u2014 from first visit to a booked sitting',
      'Developed an educational content strategy around real seeker questions: career, relationships, timing and remedies',
      'Launched considered Google Search and Meta campaigns to reach genuine consultation seekers worldwide',
      'Optimized landing pages and integrated WhatsApp booking for effortless enquiries',
    ],
    aiUse: [
      'AI-supported content ideation grounded in the astrologer\u2019s own teachings',
      'Educational posts shaped through careful prompt design',
      'AI-assisted keyword and topic research for global reach',
      'Automated, respectful lead qualification',
      'AI-assisted creative concepts for campaigns',
    ],
    results: [
      { k: '55\u201365%', v: 'More qualified consultation enquiries' },
      { k: '25\u201330%', v: 'Lower Cost Per Lead (CPL)' },
      { k: '70%+', v: 'Growth in organic website traffic' },
      { k: '20\u201325%', v: 'Higher booking conversion rate' },
    ],
    learning:
      'In trust-based practices like astrology, seekers commit when authentic teaching, clear authority and gentle, well-timed nurturing work together \u2014 far more than paid advertising alone. Speaking the client\u2019s language let us grow their reach globally and their revenue meaningfully, without compromising the soul of the practice.',
  },
  {
    slug: 'wellness-healing-practice',
    industry: 'Wellness & Healing',
    icon: Leaf,
    accent: 'cyan',
    client: 'Wellness & Healing Practice',
    location: 'Global Consultation Audience',
    objective:
      'Grow premium consultation bookings and position the practitioner as a trusted holistic wellness and healing expert to a worldwide audience.',
    practitionerNote:
      'Having spent years around the healing and occult community, I recognise how deeply personal this work is. I helped this practitioner speak to seekers with warmth and credibility, taking their brand to international audiences while keeping the human, healing heart of the practice intact.',
    challenges: [
      'Generic positioning that failed to convey the depth of the practice',
      'Low conversion of visitors into paid consultations',
      'Weak client retention and no structured way to stay connected',
      'Limited international reach despite genuine expertise',
    ],
    strategy: [
      'Repositioned the brand around real transformation and client outcomes',
      'Created educational resources and lead magnets that build trust before the first session',
      'Designed a conversion-focused consultation journey with clear service pathways',
      'Expanded reach through SEO and targeted campaigns across international audiences',
      'Built gentle email and WhatsApp nurturing sequences to guide and re-engage seekers',
    ],
    aiUse: [
      'AI-assisted content planning aligned with the practitioner\u2019s healing philosophy',
      'Audience research and messaging refinement for global markets',
      'AI-generated visual concepts for wellness campaigns',
      'Marketing automation for nurturing and appointment reminders',
      'AI-enhanced performance insights for continuous improvement',
    ],
    results: [
      { k: '40\u201350%', v: 'More consultation bookings' },
      { k: '22\u201328%', v: 'Lower client acquisition cost' },
      { k: '60%', v: 'Wider international audience reach' },
      { k: '30%', v: 'Stronger repeat client engagement' },
    ],
    learning:
      'Educational content paired with thoughtful, AI-assisted nurturing dramatically improves trust and long-term retention in healing practices \u2014 the same qualities that let this brand grow its revenue and its global community together.',
  },
  {
    slug: 'vastu-consultant',
    industry: 'Vastu Consulting',
    icon: Compass,
    accent: 'purple',
    client: 'Vastu Consultant Digital Transformation',
    location: 'Residential · Commercial · International Clients',
    objective:
      'Modernize a respected Vastu consultant\u2019s digital presence and generate qualified residential and commercial consultation leads across local and international clients.',
    practitionerNote:
      'I practise Vastu consulting myself, so I know how much tradition and intuition sit behind every recommendation. That let me translate this consultant\u2019s knowledge into a modern, credible brand \u2014 expanding their reach globally and their revenue, while honouring the discipline behind the work.',
    challenges: [
      'Minimal digital visibility despite a strong offline reputation',
      'An outdated website that undersold the depth of the service',
      'Poor local and organic search rankings',
      'No clear, guided consultation process for new enquiries',
    ],
    strategy: [
      'Rebuilt the website with a modern experience that respects the tradition of Vastu',
      'Developed SEO-rich service pages for residential and commercial consultations',
      'Created educational content to build trust and authority with seekers',
      'Implemented local SEO to strengthen visibility where clients search',
      'Launched focused Google lead campaigns to reach high-intent enquiries',
    ],
    aiUse: [
      'AI-assisted SEO and educational content creation',
      'AI-generated concepts for website and social visuals',
      'AI-powered campaign analysis and optimization',
      'Automated inquiry workflows for faster response',
      'AI-assisted reporting for clear performance insight',
    ],
    results: [
      { k: '45\u201355%', v: 'More qualified enquiries' },
      { k: '75%', v: 'Better local search visibility' },
      { k: '20\u201325%', v: 'Lower Cost Per Lead (CPL)' },
      { k: '50%', v: 'Higher website engagement' },
    ],
    learning:
      'For specialised consulting like Vastu, educational SEO and localized authority building outperform purely promotional advertising over time \u2014 and when guided by someone who understands the practice, that authority translates into global reach and lasting revenue growth.',
  },
];
